import React, { useEffect, useState } from 'react'
import {
  Box,
  Button, Checkbox,
  Container,
  FormControlLabel, FormGroup,
  Grid,
  Paper, Typography,
}                                     from '@mui/material'
import { useTranslation }             from 'react-i18next'
import { useNavigate, useParams }     from 'react-router-dom'
import { useApi }                     from '../utils/api'
import { useSnackbar }                from 'notistack'
import AlertSimpleDialog              from '../ui/components/alert-simple-dialog'
import Page                           from '../ui/components/Page'
import { BaseBreadcrumbs }            from '../ui/components/BaseBreadcrumbs'
import ShortFormControl               from '../ui/components/short-form-control'
import ShortFormSelect                from '../ui/components/short-form-select'
import Consts                         from '../Consts'

export default function SharePage() {

  const api = useApi()
  const navigate = useNavigate()
  const { serviceId, shareId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [operators, setOperators] = useState([])
  const [service, setService] = useState(null)
  const [values, setValues] = useState({
    name: '',
    serviceId: serviceId,
    operatorId: '',
    type: '',
    transactionType: Consts.Share.transactionTypePartnerShare,
    variant: Consts.Share.variantPrice,
    value: '',
    mtValue: '',
    mediatexShare: false,
    position: '',
  })
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)

  useEffect(() => {
    api.operator.list().then((response) => setOperators(response.data)).catch(err => {
      console.error(err)
    })
    api.service.detail(serviceId).then((response) => setService(response)).catch((err) => {
      console.error(err)
    })
  }, [])

  useEffect(() => {
    (async () => {
      if (!isNaN(shareId)) {
        try{
          let share = await api.share.detail(shareId)
          setValues({
            ...share,
            value: share.variant === Consts.Share.variantPercent ? share.value * 100 : share.value,
            mtValue: share.variant === Consts.Share.variantPercent && share.mediatexShare ? share.mtValue * 100 : share.mtValue,
          })
        } catch (e){
          enqueueSnackbar(t('Can\'t load share'), {
            variant: "error"
          })
        }
      }
    })()
  }, [shareId])

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.share.delete(shareId).then(() => {
        enqueueSnackbar(t('Service share deleted'), {
          variant: 'success'
        })
        navigate(`/services/${serviceId}`)
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete service share'), {
          variant: 'error'
        })
      })
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    let data = {
      ...values,
      serviceId: parseInt(serviceId),
    }
    data.value = data.value ? parseFloat(`${data.value}`.replaceAll(',','.')) : 0
    data.mtValue = data.mtValue ? parseFloat(`${data.mtValue}`.replaceAll(',','.')) : 0
    if (values.variant === Consts.Share.variantPercent) {
      data.value = data.value / 100
      data.mtValue = data.mtValue / 100
    }
    try {
      let share = await api.share.save(data)
      navigate(`/services/${serviceId}/share/${share.id}`)
      enqueueSnackbar(t('Share saved'), {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(t('Failed to save service share'), {
        variant: 'error'
      })
    }
  }

  return (
    <Page title={t('Service share')}>
      <Container component="main">
        <BaseBreadcrumbs key={'breadcrumb-share'}
                         title={values?.name ? values.name : t('Service share')}
                         items={[
                           { title: t('Services'), href: `/services` },
                           { title: service?.title ?? t('Service'), href: `/services/${serviceId}` }
                         ]}/>
        <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
          <Paper sx={{ p: 2, width: '100%' }}>
            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12}>
                <ShortFormControl name={'name'} title={t('Name')} value={values.name ?? ''} handleChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                {operators.length > 0 && <ShortFormSelect name={'operatorId'} title={t('Operator')} value={values.operatorId} handleChange={handleChange} items={operators.map(it => ({ id: it.id, name: it.name }))} />}
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormSelect name={'type'} title={t('Share type')} value={values?.type} handleChange={handleChange} items={[
                  {id: Consts.Share.typePartner, name: t('Partner')},
                  {id: Consts.Share.typeOperator, name: t('Operator')},
                ]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormSelect name={'transactionType'} title={t('Share transaction type')} value={values?.transactionType} handleChange={handleChange} allowAllOption={false} items={[
                  // {id: Consts.Share.transactionTypeVat, name: t('transactionType_vat_fee')}, // do not included in options - generated automatically
                  // {id: Consts.Share.transactionTypeMediatexFee, name: t('transactionType_mediatex_fee')}, // do not included in options - generated automatically
                  // {id: Consts.Share.transactionTypeMediatexShare, name: t('transactionType_mediatex_share')}, // do not included in options - generated automatically
                  {id: Consts.Share.transactionTypeOperatorFee, name: t('transactionType_operator_fee')},
                  {id: Consts.Share.transactionTypeOperatorShare, name: t('transactionType_operator_share')},
                  {id: Consts.Share.transactionTypePartnerFee, name: t('transactionType_partner_fee')},
                  {id: Consts.Share.transactionTypePartnerShare, name: t('transactionType_partner_share')},
                ]} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormControl name={'position'} title={t('Share position')} value={values?.position ?? ''} handleChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormSelect name={'variant'} title={t('Share variant')} value={values?.variant} handleChange={handleChange} allowAllOption={false} items={[
                  {id: Consts.Share.variantPrice, name: t('Price share')},
                  {id: Consts.Share.variantPercent, name: t('Percent share')},
                ]} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormControl name={'value'} title={t('Share value')} value={values?.value ?? ''} handleChange={handleChange} />
                <Typography variant={'caption'}>{values?.variant === Consts.Share.variantPrice ? t(`share-price-hint`) : t('share-percent-hint')}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <ShortFormControl name={'mtValue'} title={t('Share mt value')} value={(!values?.mediatexShare ? 0 : values?.mtValue) ?? ''} handleChange={handleChange} inputProps={{ disabled: !values?.mediatexShare }} />
                <FormGroup>
                  <FormControlLabel control={<Checkbox/>} checked={values?.mediatexShare ?? false} onChange={(e) => {
                    handleChange('mediatexShare')({ target: { value: e.target.checked } })
                  }} label={t('Share mt ratio')}/>
                </FormGroup>
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ mt: 2, px: 1 }}>
            <Button type={'submit'} variant={'contained'}>{t('Save')}</Button>
            { !isNaN(shareId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
          </Box>
        </Grid>
        <AlertSimpleDialog
          title={t('Deleting share')}
          message={t('delete-confirm-msg', { name: values.id })}
          open={shownDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
      </Container>
    </Page>
  )
}